<template>
	<div
		class="relative w-full h-full flex items-center justify-center bg-black/5"
	>
		<!-- Uploaded mp4: native controls in the gallery, muted first frame as thumb. -->
		<video
			v-if="kind === 'video' && src"
			:controls="!thumbnail"
			muted
			playsinline
			preload="metadata"
			class="w-full h-full"
			:class="thumbnail ? 'object-cover pointer-events-none' : 'object-contain'"
		>
			<source :src="src" type="video/mp4" />
		</video>

		<!-- Embedded YouTube. The aspect box gives the iframe a real height — an
			absolutely-positioned iframe in a percentage-height parent collapses to
			zero (audio plays, nothing shows). -->
		<div
			v-else-if="kind === 'youtube' && youtubeId"
			class="relative w-full"
			:class="thumbnail ? 'h-full' : 'aspect-video max-h-full'"
		>
			<!-- Poster (thumbnail, or before the user presses play). -->
			<NuxtImg
				v-if="thumbnail || !iframeLoaded"
				:src="`https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`"
				:alt="file.Name || ''"
				loading="lazy"
				class="absolute inset-0 w-full h-full object-cover"
			/>
			<!-- Iframe loaded only on click (privacy + LCP). -->
			<iframe
				v-else
				:src="`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&rel=0`"
				:title="file.Name || ''"
				class="absolute inset-0 w-full h-full border-0"
				allow="
					accelerometer;
					autoplay;
					encrypted-media;
					gyroscope;
					picture-in-picture;
				"
				allowfullscreen
			/>
			<!-- Click-to-play affordance for the full YouTube slide. -->
			<button
				v-if="!thumbnail && !iframeLoaded"
				type="button"
				class="absolute inset-0 flex items-center justify-center cursor-pointer group"
				:aria-label="$t('Play video')"
				@click="iframeLoaded = true"
			>
				<span :class="badgeClass">
					<svg
						xmlns="http://www.w3.org/2000/svg"
						viewBox="0 0 24 24"
						fill="white"
						class="w-8 h-8 translate-x-[2px]"
					>
						<path d="M8 5v14l11-7z" />
					</svg>
				</span>
			</button>
		</div>

		<!-- Decorative play badge on thumbnails (mp4 + YouTube). Parent handles
			selection, so it stays click-through. -->
		<span
			v-if="thumbnail && kind !== 'image'"
			class="absolute inset-0 flex items-center justify-center pointer-events-none"
			aria-hidden="true"
		>
			<span :class="badgeClass">
				<svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 24 24"
					fill="white"
					class="w-4 h-4 translate-x-[1px]"
				>
					<path d="M8 5v14l11-7z" />
				</svg>
			</span>
		</span>
	</div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import type { ProductFile } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import {
	getProductMediaKind,
	getEmbeddedLink,
	getYoutubeId
} from "~/utils/product/productMedia";

interface Props {
	file: ProductFile;
	thumbnail?: boolean;
}
const { file, thumbnail = false } = defineProps<Props>();

const kind = computed(() => getProductMediaKind(file));
const src = computed(() => file.Path ?? "");
const youtubeId = computed(() => getYoutubeId(getEmbeddedLink(file)));
const iframeLoaded = ref(false);

const badgeClass = computed(() => [
	"rounded-full bg-black/70 flex items-center justify-center transition group-hover:bg-black/85",
	thumbnail ? "w-8 h-8" : "w-16 h-16"
]);
</script>
